import { useEffect, useState, useCallback, useMemo } from "react";
import Axios from "../../../utils/axios";
import AnimationLogo from "../../../components/Layout/LoadingAnimation/AnimationLogo";
import { IEvent } from "../../../interfaces/models/IEvent";
import Input from "../../../components/Atomos/Inputs/Input";
import { uploadImage } from "../../../utils/UploadImage";

interface IErrorState {
  eventRequired: boolean;
  photosRequired: boolean;
  errorMessage?: string;
}

const initialErrorState: IErrorState = {
  eventRequired: false,
  photosRequired: false,
  errorMessage: '',
};

interface PhotoFile {
  file: File;
  preview: string;
  id: string;
}

const AddEventPhotoGallery: React.FC = () => {
  const axios = useMemo(() => new Axios(), []);
  const [errorState, setErrorState] = useState<IErrorState>(initialErrorState);
  const [selectedPhotos, setSelectedPhotos] = useState<PhotoFile[]>([]);
  const [events, setEvents] = useState<IEvent[]>([]);
  const [selectedEvents, setSelectedEvents] = useState<IEvent[]>([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filteredEvents, setFilteredEvents] = useState<IEvent[]>([]);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState<{ current: number; total: number; currentEvent: string } | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [appendMode, setAppendMode] = useState(true); // true = append, false = replace

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.Get('./event');
      const data = response.data;
      if (data != null) {
        const eventsArray = Array.isArray(data) ? data : [];
        setEvents(eventsArray);
        setFilteredEvents(eventsArray);
      }
    } catch (error) {
      console.error('Error fetching events:', error);
    }
  }, [axios]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  useEffect(() => {
    if (searchTerm.trim() === '') {
      setFilteredEvents(events);
    } else {
      const filtered = events.filter(event =>
        event.title?.toLowerCase().includes(searchTerm.toLowerCase()) ||
        event.shortId?.toLowerCase().includes(searchTerm.toLowerCase())
      );
      setFilteredEvents(filtered);
    }
  }, [searchTerm, events]);

  const uploadEventPhotoGallery = useCallback(async (
    photoUrls: string[],
    event: IEvent
  ) => {
    try {
      console.log('Updating event photo gallery in DB:', {
        eventId: event.id,
        eventTitle: event.title,
        photoUrls: photoUrls,
        appendMode: appendMode
      });

      const eventToUpdate: any = {
        ...event,
      };

      // Handle append vs replace mode
      if (appendMode) {
        // Append new photos to existing gallery
        const existingPhotos = event.photoGallery || [];
        eventToUpdate.photoGallery = [...existingPhotos, ...photoUrls];
      } else {
        // Replace existing gallery with new photos
        eventToUpdate.photoGallery = photoUrls;
      }
      
      const response = await axios.Put('./event', eventToUpdate);
      console.log('Event photo gallery updated successfully:', response);
      
      return true;
    } catch (error: any) {
      console.error('Error updating event photo gallery:', error);
      throw error;
    }
  }, [axios, appendMode]);

  const validateFile = (file: File): boolean => {
    const isPNG = file.type === 'image/png' || file.name.toLowerCase().endsWith('.png');
    const isJPG = file.type === 'image/jpeg' || file.name.toLowerCase().endsWith('.jpg') || file.name.toLowerCase().endsWith('.jpeg');
    const isGIF = file.type === 'image/gif' || file.name.toLowerCase().endsWith('.gif');
    
    if (!isPNG && !isJPG && !isGIF) {
      setErrorState({
        ...errorState,
        photosRequired: true,
        errorMessage: 'Only PNG, JPG, or GIF files are allowed'
      });
      return false;
    }

    if (file.size > 10 * 1024 * 1024) { // 10MB limit
      setErrorState({
        ...errorState,
        photosRequired: true,
        errorMessage: 'File size must be less than 10MB'
      });
      return false;
    }

    return true;
  };

  const handleFileSelect = (files: FileList | null) => {
    if (!files || files.length === 0) {
      return;
    }

    const filesToProcess: File[] = [];
    
    Array.from(files).forEach((file) => {
      if (validateFile(file)) {
        filesToProcess.push(file);
      }
    });

    if (filesToProcess.length === 0) {
      return;
    }

    // Process each file and add to state as it's processed
    filesToProcess.forEach((file) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const photoFile: PhotoFile = {
          file,
          preview: e.target?.result as string,
          id: `${Date.now()}-${Math.random()}`
        };
        setSelectedPhotos(prev => [...prev, photoFile]);
      };
      reader.readAsDataURL(file);
    });

    setErrorState({ ...errorState, photosRequired: false, errorMessage: undefined });
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleFileInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const files = e.target.files;
    if (files && files.length > 0) {
      handleFileSelect(files);
    }
  };

  const handleRemovePhoto = (photoId: string) => {
    setSelectedPhotos(prev => prev.filter(photo => photo.id !== photoId));
    setErrorState({ ...errorState, photosRequired: false, errorMessage: undefined });
  };

  const handleClearAllPhotos = () => {
    setSelectedPhotos([]);
    setErrorState({ ...errorState, photosRequired: false, errorMessage: undefined });
  };

  const handleSubmit = useCallback(async () => {
    const errors = {
      ...initialErrorState,
      eventRequired: selectedEvents.length === 0,
      photosRequired: selectedPhotos.length === 0,
      errorMessage: selectedPhotos.length === 0 ? 'Please select at least one photo to upload' : undefined
    };

    setErrorState(errors);

    if (selectedEvents.length === 0 || selectedPhotos.length === 0) {
      return;
    }

    try {
      setUploading(true);
      setUploadProgress({ current: 0, total: selectedEvents.length, currentEvent: '' });
      
      // Sanitize filenames - remove special characters
      const sanitizeFileName = (fileName: string) => {
        return fileName
          .toLowerCase()
          .replace(/[^a-z0-9.]/g, '-') // Replace any non-alphanumeric (except dots) with hyphen
          .replace(/-+/g, '-') // Replace multiple hyphens with single hyphen
          .replace(/^-|-$/g, ''); // Remove leading/trailing hyphens
      };
      
      console.log('Uploading photos to', selectedEvents.length, 'events');
      
      let successCount = 0;
      let errorCount = 0;
      
      // Process each selected event
      for (let i = 0; i < selectedEvents.length; i++) {
        const event = selectedEvents[i];
        
        setUploadProgress({ 
          current: i + 1, 
          total: selectedEvents.length, 
          currentEvent: event.title || event.shortId || 'Unknown Event'
        });
        
        try {
          console.log(`Uploading photos to event ${i + 1}/${selectedEvents.length}: ${event.title}`);
          
          // Upload all selected photos to Azure Storage for each event
          // Path: UAT/events/[eventId]/[timestamp][sanitized-filename].png
          const uploadPromises = selectedPhotos.map(({ file }) => {
            const sanitizedFileName = sanitizeFileName(file.name);
            const sanitizedFile = new File([file], sanitizedFileName, { type: file.type });
            return uploadImage(sanitizedFile, event.id!, 'events/');
          });
          
          const uploadResults = await Promise.all(uploadPromises);
          
          // Filter out any undefined/null results
          const photoUrls = uploadResults.filter((url): url is string => url !== undefined && url !== null);

          if (photoUrls.length === 0) {
            throw new Error('No photos were successfully uploaded');
          }

          // Update event with uploaded photos
          await uploadEventPhotoGallery(photoUrls, event);
          successCount++;
        } catch (error: any) {
          console.error(`Error uploading photos to event ${event.title}:`, error);
          errorCount++;
        }
      }
      
      setUploading(false);
      setUploadProgress(null);
      
      // Show success message
      if (successCount > 0) {
        setErrorState({
          ...initialErrorState,
          errorMessage: successCount === selectedEvents.length
            ? `✓ Successfully uploaded ${selectedPhotos.length} photo${selectedPhotos.length > 1 ? 's' : ''} to ${successCount} event${successCount > 1 ? 's' : ''}`
            : `✓ Uploaded to ${successCount} event${successCount > 1 ? 's' : ''}, ${errorCount} error${errorCount > 1 ? 's' : ''}`
        });
        
        // Clear selection after successful upload
        setTimeout(() => {
          setSelectedEvents([]);
          setSearchTerm('');
          setSelectedPhotos([]);
          setErrorState(initialErrorState);
          
          // Refresh events list
          fetchEvents();
        }, 2000);
      } else {
        setErrorState({
          ...initialErrorState,
          errorMessage: `Error uploading photos to all events. Please try again.`
        });
      }
    } catch (error: any) {
      console.error('Error uploading photos:', error);
      setErrorState((prev) => ({
        ...prev,
        errorMessage: 'Error uploading photos. Please try again.'
      }));
      setUploading(false);
      setUploadProgress(null);
    }
  }, [selectedEvents, selectedPhotos, uploadEventPhotoGallery, fetchEvents]);

  return (
    <main className="mainSection p-8">
      {uploading && (
        <div className="absolute top-0 left-0">
          <AnimationLogo />
        </div>
      )}
      
      <div className="max-w-6xl mx-auto h-full">
        <h1 className="text-3xl font-bold text-gray-font mb-2">Add Event Photo Gallery</h1>
        <p className="text-gray-500 mb-8">Admin panel to upload photos to event galleries</p>

        <div className="bg-white rounded-lg shadow-md p-6">
          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Search Event
            </label>
            <Input
              onChange={(e) => setSearchTerm(e.target.value)}
              name="search"
              title="Search by event name or ID"
              value={searchTerm}
              error={errorState.eventRequired}
            />
          </div>

          <div className="mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Events {errorState.eventRequired && <span className="text-feedback-error">*Required</span>}
              {selectedEvents.length > 0 && (
                <span className="ml-2 text-sm text-gray-500">
                  ({selectedEvents.length} selected)
                </span>
              )}
            </label>
            <div className="mb-2 flex gap-2">
              <button
                onClick={() => {
                  if (selectedEvents.length === filteredEvents.length) {
                    setSelectedEvents([]);
                  } else {
                    setSelectedEvents([...filteredEvents]);
                  }
                }}
                className="text-sm px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
              >
                {selectedEvents.length === filteredEvents.length ? 'Deselect All' : 'Select All'}
              </button>
            </div>
            <div className="border rounded-md max-h-64 overflow-y-auto">
              {filteredEvents.length === 0 ? (
                <div className="p-4 text-gray-500 text-center">Check back soon for upcoming events.</div>
              ) : (
                filteredEvents.map((event) => {
                  const isSelected = selectedEvents.some(e => e.id === event.id);
                  const photoCount = event.photoGallery?.length || 0;
                  return (
                    <div
                      key={event.id}
                      onClick={() => {
                        if (isSelected) {
                          setSelectedEvents(selectedEvents.filter(e => e.id !== event.id));
                        } else {
                          setSelectedEvents([...selectedEvents, event]);
                        }
                      }}
                      className={`p-4 cursor-pointer hover:bg-gray-50 border-b last:border-b-0 flex items-center gap-4 ${
                        isSelected ? 'bg-primary-light' : ''
                      }`}
                    >
                      <input
                        type="checkbox"
                        checked={isSelected}
                        onChange={() => {
                          if (isSelected) {
                            setSelectedEvents(selectedEvents.filter(e => e.id !== event.id));
                          } else {
                            setSelectedEvents([...selectedEvents, event]);
                          }
                        }}
                        onClick={(e) => e.stopPropagation()}
                        className="w-4 h-4 text-primary border-gray-300 rounded focus:ring-primary"
                      />
                      <div className="flex-1">
                        <div className="font-semibold">{event.title || 'Untitled Event'}</div>
                        <div className="text-sm text-gray-500">
                          ID: {event.shortId || event.id} {event.eventType && `• ${event.eventType}`} {event.setupType && `• ${event.setupType}`}
                        </div>
                      </div>
                      {photoCount > 0 && (
                        <span className="text-xs px-2 py-1 rounded bg-blue-100 text-blue-800">
                          {photoCount} photo{photoCount > 1 ? 's' : ''}
                        </span>
                      )}
                    </div>
                  );
                })
              )}
            </div>
          </div>

          {selectedEvents.length > 0 && (
            <>
              <div className="mb-6 p-4 bg-blue-50 rounded-md">
                <p className="text-sm font-medium text-blue-900 mb-2">
                  Selected Events ({selectedEvents.length}):
                </p>
                <div className="flex flex-wrap gap-2">
                  {selectedEvents.map((event) => (
                    <span
                      key={event.id}
                      className="inline-flex items-center gap-1 px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm"
                    >
                      {event.title || event.shortId || 'Untitled Event'}
                      <button
                        onClick={() => {
                          setSelectedEvents(selectedEvents.filter(e => e.id !== event.id));
                        }}
                        className="ml-1 text-blue-600 hover:text-blue-800 font-bold"
                      >
                        ×
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Append/Replace Mode Toggle */}
              <div className="mb-6 p-4 bg-gray-50 rounded-md">
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Mode
                </label>
                <div className="flex gap-4">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="uploadMode"
                      checked={appendMode}
                      onChange={() => setAppendMode(true)}
                      className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">Append to existing gallery</span>
                  </label>
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input
                      type="radio"
                      name="uploadMode"
                      checked={!appendMode}
                      onChange={() => setAppendMode(false)}
                      className="w-4 h-4 text-primary border-gray-300 focus:ring-primary"
                    />
                    <span className="text-sm text-gray-700">Replace existing gallery</span>
                  </label>
                </div>
                <p className="text-xs text-gray-500 mt-2">
                  {appendMode 
                    ? 'New photos will be added to the existing gallery' 
                    : 'New photos will replace all existing photos in the gallery'}
                </p>
              </div>

              {/* Photo Upload Area */}
              <div className="mb-6">
                <div className="block text-sm font-medium text-gray-700 mb-2">
                  Upload Photos (PNG/JPG/GIF) {errorState.photosRequired && <span className="text-feedback-error">*Required</span>}
                  {selectedPhotos.length > 0 && (
                    <span className="ml-2 text-sm text-gray-500">
                      ({selectedPhotos.length} selected)
                    </span>
                  )}
                </div>
                
                {selectedPhotos.length === 0 ? (
                  <div
                    onDragOver={handleDragOver}
                    onDragLeave={handleDragLeave}
                    onDrop={handleDrop}
                    className={`relative border-2 border-dashed rounded-lg p-8 text-center transition-colors ${
                      isDragging 
                        ? 'border-primary bg-primary-light' 
                        : 'border-gray-300 hover:border-primary hover:bg-gray-50'
                    }`}
                  >
                    <input
                      type="file"
                      id="photoGalleryInput"
                      accept=".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif"
                      onChange={handleFileInputChange}
                      multiple
                      className="hidden"
                    />
                    <div className="flex flex-col items-center gap-3">
                      <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                      </svg>
                      <div>
                        <p className="text-base font-medium text-gray-700 mb-1">
                          {isDragging ? 'Drop files here' : 'Drag and drop photos'}
                        </p>
                        <p className="text-xs text-gray-500 mb-2">or</p>
                        <label
                          htmlFor="photoGalleryInput"
                          className="inline-block px-3 py-1.5 bg-primary text-white rounded-md cursor-pointer hover:bg-primary-dark transition-colors text-sm"
                        >
                          Browse Files
                        </label>
                      </div>
                      <p className="text-xs text-gray-400">PNG, JPG, or GIF files only, max 10MB each</p>
                    </div>
                  </div>
                ) : (
                  <div className="space-y-4">
                    <div className="flex items-center justify-between mb-2">
                      <p className="text-sm font-medium text-gray-700">
                        {selectedPhotos.length} photo{selectedPhotos.length > 1 ? 's' : ''} selected
                      </p>
                      <div className="flex gap-2">
                        <label
                          htmlFor="photoGalleryInputAdd"
                          className="text-sm px-3 py-1 bg-primary text-white rounded cursor-pointer hover:bg-primary-dark transition-colors"
                        >
                          Add More
                        </label>
                        <input
                          type="file"
                          id="photoGalleryInputAdd"
                          accept=".png,.jpg,.jpeg,.gif,image/png,image/jpeg,image/gif"
                          onChange={handleFileInputChange}
                          multiple
                          className="hidden"
                        />
                        <button
                          onClick={handleClearAllPhotos}
                          className="text-sm px-3 py-1 bg-red-500 text-white rounded hover:bg-red-600 transition-colors"
                        >
                          Clear All
                        </button>
                      </div>
                    </div>
                    <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
                      {selectedPhotos.map((photo) => (
                        <div key={photo.id} className="relative border-2 border-green-500 rounded-lg p-2 bg-green-50">
                          <div className="relative aspect-square overflow-hidden rounded-lg bg-white">
                            <img 
                              src={photo.preview} 
                              alt={`Preview ${photo.id}`} 
                              className="w-full h-full object-cover"
                            />
                            <button
                              onClick={() => handleRemovePhoto(photo.id)}
                              className="absolute top-1 right-1 bg-red-500 text-white rounded-full w-6 h-6 flex items-center justify-center hover:bg-red-600 transition-colors text-xs font-bold"
                            >
                              ×
                            </button>
                          </div>
                          <p className="text-xs text-gray-600 mt-1 truncate" title={photo.file.name}>
                            {photo.file.name}
                          </p>
                          <p className="text-xs text-gray-400">
                            {(photo.file.size / 1024).toFixed(2)} KB
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </div>

              {uploadProgress && (
                <div className="mb-4 p-4 bg-blue-50 border border-blue-200 rounded-md">
                  <div className="flex items-center justify-between mb-2">
                    <p className="text-sm font-medium text-blue-900">
                      Uploading to {uploadProgress.currentEvent}...
                    </p>
                    <p className="text-sm text-blue-700">
                      {uploadProgress.current} / {uploadProgress.total}
                    </p>
                  </div>
                  <div className="w-full bg-blue-200 rounded-full h-2">
                    <div
                      className="bg-blue-600 h-2 rounded-full transition-all duration-300"
                      style={{ width: `${(uploadProgress.current / uploadProgress.total) * 100}%` }}
                    />
                  </div>
                </div>
              )}

              {errorState.errorMessage && (
                <div className={`mb-4 p-3 rounded-md ${
                  errorState.errorMessage.startsWith('✓')
                    ? 'bg-green-50 border border-green-200'
                    : 'bg-red-50 border border-red-200'
                }`}>
                  <p className={`text-sm ${
                    errorState.errorMessage.startsWith('✓')
                      ? 'text-green-800'
                      : 'text-red-800'
                  }`}>
                    {errorState.errorMessage}
                  </p>
                </div>
              )}

              <button
                onClick={handleSubmit}
                disabled={uploading || selectedPhotos.length === 0 || selectedEvents.length === 0}
                className={`w-full py-3 px-6 rounded-md font-semibold transition-colors ${
                  uploading || selectedPhotos.length === 0 || selectedEvents.length === 0
                    ? 'bg-gray-300 text-gray-500 cursor-not-allowed'
                    : 'bg-primary text-white hover:bg-primary-dark'
                }`}
              >
                {uploading 
                  ? `Uploading ${selectedPhotos.length} photo${selectedPhotos.length > 1 ? 's' : ''} to ${selectedEvents.length} event${selectedEvents.length > 1 ? 's' : ''}...` 
                  : `Upload ${selectedPhotos.length} Photo${selectedPhotos.length > 1 ? 's' : ''} to ${selectedEvents.length} Event${selectedEvents.length > 1 ? 's' : ''}`
                }
              </button>
            </>
          )}
        </div>
      </div>
    </main>
  );
};

export default AddEventPhotoGallery;

